import {
  put,
  call,
  takeLatest,
  select,
  fork,
  join,
  delay,
  retry,
  race,
  take,
} from 'redux-saga/effects';
import orderApi from '../api/order.api';
import * as ActionTypes from '../actions/types.action';
import {getBearerHeader} from '../helpers/api.helper';
import {waitFor} from '../helpers/saga.helper';
import {
  AX_BASKET_ERRORS,
  AX_ORDER_STATUS,
  ERRORS,
  MAX_ORDER_RETRY,
  GET_ORDER_RETRY_TIMEOUT,
  ROUTES,
} from '../constants/constants';
import {
  isLessThanAWeekOldByDate,
  isLessThanAWeekOld,
} from '../helpers/array.helper';
import {
  isPayByBankAwaitingBank,
  isStaleOrderUpdate,
} from '../helpers/order.helper';

const getCurrentOrder = state => state.order;

/**
 * Puts the fetched order unless the order in state is a newer copy of it: a
 * slow re-read of a pending order can land after the notifier's refetch has
 * already moved it on, and must not put it back to PAYMENT_PENDING.
 */
function* putFetchedOrder(order) {
  const current = yield select(getCurrentOrder);
  if (isStaleOrderUpdate(order, current)) {
    yield put({type: ActionTypes.GET_ORDER_AFTER_CHECKOUT_IGNORED, order});
    return;
  }
  yield put({type: ActionTypes.GET_ORDER_SUCCEEDED, order});
}

function* getOrder(action) {
  try {
    if (!action.orderId) {
      return;
    }
    const getAuth = state => state.auth;
    const storeToken = yield select(getAuth);
    const headers = getBearerHeader(action.jjToken, storeToken);
    if (!headers) {
      yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
      return;
    }
    const order = yield call(orderApi.getOrder, action.orderId, headers);
    if (order.status === AX_ORDER_STATUS.EDIT_EXPIRED) {
      yield put({
        type: ActionTypes.AX_BASKET_ALREADY_PICKED,
        message: AX_BASKET_ERRORS.STARTED_PICKING,
      });
    } else {
      yield* putFetchedOrder(order);
    }
  } catch (e) {
    yield put({
      type: ActionTypes.GET_ORDER_FAILED,
      message: e.message,
    });
  }
}

function* getNewOrder(orderId, headers) {
  try {
    const order = yield call(orderApi.getOrder, orderId, headers);
    if (order) {
      return order;
    }
  } catch (e) {
    // The api helper has already tried to refresh the token by now.
    if (
      e &&
      (e.code === ERRORS.NEED_AUTH ||
        e.error === ERRORS.EXPIRED ||
        e.error === ERRORS.UNAUTHORIZED)
    ) {
      return ERRORS.ORDER_UNAUTHORIZED;
    }
    return ERRORS.ORDER_FAILED;
  }
}

const isFetchedOrder = order => !!order && typeof order === 'object';

function* reAuthOrderPayment(action) {
  try {
    const getAuth = state => state.auth;
    const storeToken = yield select(getAuth);
    const headers = getBearerHeader(action.jjToken, storeToken);
    if (!headers) {
      yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
      return;
    }
    const results = yield call(orderApi.reAuthOrder, action.orderId, headers);
    if (results) {
      yield put({
        type: ActionTypes.PROCESS_CHECKOUT_SUCCESS,
        checkout: results,
      });
      yield put({
        type: ActionTypes.REAUTH_ORDER_SUCCEEDED,
      });
    }
  } catch (e) {
    yield put({
      type: ActionTypes.REAUTH_ORDER_FAILED,
      message: e.message,
    });
  }
}

function* getFinalOrder(action) {
  try {
    const getAuth = state => state.auth;
    const storeToken = yield select(getAuth);
    const headers = getBearerHeader(action.jjToken, storeToken);
    let order;
    let i = 0;
    // Set when a poll finds the order in a settled status. The retry counter
    // cannot tell that apart from running out of retries: a poll that settles
    // on the last attempt used to be reported as a failure.
    let settled = false;
    order = yield call(getNewOrder, action.orderId, headers);
    if (order === ERRORS.ORDER_UNAUTHORIZED) {
      yield put({type: ActionTypes.GET_ORDER_AFTER_CHECKOUT_UNAUTHORIZED});
      yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
      return;
    }
    if (!isFetchedOrder(order)) {
      const current = yield select(getCurrentOrder);
      if (
        current &&
        current.orderId === action.orderId &&
        (current.status === AX_ORDER_STATUS.PAYMENT_PENDING ||
          isPayByBankAwaitingBank(current))
      ) {
        // A failed re-read of an order already on screen as pending: keep it,
        // the confirmation page reads it again shortly. Only a first read that
        // keeps failing is reported as a problem.
        yield put({type: ActionTypes.GET_ORDER_AFTER_CHECKOUT_IGNORED});
        return;
      }
    }
    if (order.status === AX_ORDER_STATUS.SLOT_EXPIRED) {
      yield put({
        type: ActionTypes.CHECKOUT_SLOT_EXPIRED,
        page: ROUTES.CHECKOUT,
      });
      return;
    }
    if (order.status === AX_ORDER_STATUS.EDIT_EXPIRED) {
      yield put({
        type: ActionTypes.AX_BASKET_ALREADY_PICKED,
        message: AX_BASKET_ERRORS.STARTED_PICKING,
      });
      return;
    }
    if (order.status === AX_ORDER_STATUS.TOTAL_DISCREPANCY) {
      yield put({
        type: ActionTypes.CHECKOUT_TOTAL_DISCREPANCY,
        page: ROUTES.BASKET,
      });
      return;
    }
    // A draft awaiting payment or submission has no status yet; keep polling
    // until it gets one. A failed first read is retried the same way.
    if (order && !order.status) {
      while (i < MAX_ORDER_RETRY) {
        yield delay(GET_ORDER_RETRY_TIMEOUT);
        i += 1;
        const orderFork = yield fork(getNewOrder, action.orderId, headers);
        let updatedOrder = yield join(orderFork);
        if (updatedOrder === ERRORS.ORDER_UNAUTHORIZED) {
          yield put({type: ActionTypes.GET_ORDER_AFTER_CHECKOUT_UNAUTHORIZED});
          yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
          return;
        }
        if (isFetchedOrder(updatedOrder)) {
          if (updatedOrder.status === AX_ORDER_STATUS.SLOT_EXPIRED) {
            yield put({
              type: ActionTypes.CHECKOUT_SLOT_EXPIRED,
              page: ROUTES.CHECKOUT,
            });
            break;
          }
          if (updatedOrder.status === AX_ORDER_STATUS.EDIT_EXPIRED) {
            yield put({
              type: ActionTypes.AX_BASKET_ALREADY_PICKED,
              message: AX_BASKET_ERRORS.STARTED_PICKING,
            });
            break;
          }
          // Stop on any status. Only a missing status (still a draft) means
          // the order is still being paid for or submitted. D365 now creates
          // the order within seconds of SUBMITTED, so a fixed allow-list that
          // only knew SUBMITTED kept polling through CREATED and APPROVED
          // until it ran out of retries and showed "Sorry there has been a
          // problem" for an order that was fine on refresh. PAYMENT_PENDING
          // and PAYMENT_PENDING_EXPIRED stop here too: the page explains
          // those instead of timing out.
          if (updatedOrder.status) {
            order = updatedOrder;
            settled = true;
            break;
          }
        }
      }
    }
    if (
      isFetchedOrder(order) &&
      order.orderId &&
      // A Pay By Bank order still without a status after the retries is not a
      // failed checkout: the provider's notification has not reached us yet,
      // and may take minutes. Hand it to the page, which keeps re-reading it,
      // rather than tell the customer to check out (and pay) again.
      (settled || i < MAX_ORDER_RETRY || isPayByBankAwaitingBank(order))
    ) {
      yield* putFetchedOrder(order);
    } else {
      yield put({type: ActionTypes.GET_ORDER_AFTER_CHECKOUT_FAILED});
    }
  } catch (e) {
    yield put({
      type: ActionTypes.GET_ORDER_AFTER_CHECKOUT_FAILED,
      message: e.message,
    });
  }
}

function* getOrderHistory(action) {
  try {
    if (!action.accountId) {
      return;
    }
    const getAuth = state => state.auth;
    const storeToken = yield select(getAuth);
    const headers = getBearerHeader(action.jjToken, storeToken);
    if (!headers) {
      yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
      return;
    }
    let params = {
      accountId: action.accountId,
      size: 20,
    };
    if (action.page !== undefined && action.page !== null) {
      params = Object.assign({}, params, {page: action.page});
    }
    if (action.dateFrom) {
      params = Object.assign({}, params, {dateFrom: action.dateFrom});
    }
    if (action.dateTo) {
      params = Object.assign({}, params, {dateTo: action.dateTo});
    }

    const invoicesFork = yield fork(orderApi.getInvoices, headers, params);
    const invoices = yield join(invoicesFork);
    if (invoices.error) {
      yield put({
        type: ActionTypes.GET_INVOICES_FAILED,
        message: invoices.error,
      });
    } else {
      // Process invoices to add couldBeRefunded flag
      if (
        invoices._embedded &&
        invoices._embedded.invoiceDtoes &&
        Array.isArray(invoices._embedded.invoiceDtoes)
      ) {
        invoices._embedded.invoiceDtoes = invoices._embedded.invoiceDtoes.map(
          invoice => {
            if (invoice.invoiceDateTime) {
              invoice.couldBeRefunded = isLessThanAWeekOld(
                invoice.invoiceDateTime
              );
            }
            return invoice;
          }
        );
      }

      yield put({
        type:
          action.type === ActionTypes.GET_ORDER_HISTORY_REQUESTED
            ? ActionTypes.GET_INVOICES_SUCCESS
            : ActionTypes.GET_MORE_INVOICES_SUCCESS,
        invoices,
      });
    }
  } catch (e) {
    if (e.error === ERRORS.UNAUTHORIZED) {
      yield put({type: ActionTypes.ERROR_LOGIN_EXPIRED});
    } else if (e.error === ERRORS.EXPIRED || e.code === ERRORS.NEED_AUTH) {
      yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
    }
    yield put({
      type: ActionTypes.GET_ORDER_HISTORY_FAILED,
      message: e.message,
    });
  }
}

function* getCanceledOrders(action) {
  try {
    if (!action.accountId) {
      return;
    }
    const getAuth = state => state.auth;
    const storeToken = yield select(getAuth);
    const headers = getBearerHeader(action.jjToken, storeToken);
    if (!headers) {
      yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
      return;
    }
    let params = {
      accountId: action.accountId,
      size: 20,
    };
    if (action.page !== undefined && action.page !== null) {
      params = Object.assign({}, params, {page: action.page});
    }
    if (action.dateFrom) {
      params = Object.assign({}, params, {dateFrom: action.dateFrom});
    }
    if (action.dateTo) {
      params = Object.assign({}, params, {dateTo: action.dateTo});
    }

    const cancelledOrders = yield call(
      orderApi.getOrderHistory,
      headers,
      params,
      'cancelled'
    );

    if (cancelledOrders.error) {
      yield put({
        type: ActionTypes.GET_CANCELED_ORDERS_FAILED,
        message: cancelledOrders.error,
      });
    } else {
      yield put({
        type: ActionTypes.GET_CANCELED_ORDERS_SUCCESS,
        cancelledOrders,
      });
    }
  } catch (e) {
    if (e.error === ERRORS.UNAUTHORIZED) {
      yield put({type: ActionTypes.ERROR_LOGIN_EXPIRED});
    } else if (e.error === ERRORS.EXPIRED || e.code === ERRORS.NEED_AUTH) {
      yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
    }
    yield put({
      type: ActionTypes.GET_CANCELED_ORDERS_FAILED,
      message: e.message,
    });
  }
}

function* getActiveOrders(action) {
  try {
    if (!action.accountId) {
      return;
    }
    const getAuth = state => state.auth;
    const storeToken = yield select(getAuth);
    const headers = getBearerHeader(action.jjToken, storeToken);
    if (!headers) {
      yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
      return;
    }
    let params = {
      accountId: action.accountId,
      size: 20,
    };
    if (action.page !== undefined && action.page !== null) {
      params = Object.assign({}, params, {page: action.page});
    }
    if (action.dateFrom) {
      params = Object.assign({}, params, {dateFrom: action.dateFrom});
    }
    if (action.dateTo) {
      params = Object.assign({}, params, {dateTo: action.dateTo});
    }

    const activeOrders = yield call(
      orderApi.getOrderHistory,
      headers,
      params,
      'open'
    );
    if (activeOrders.error) {
      yield put({
        type: ActionTypes.GET_ACTIVE_ORDERS_FAILED,
        message: activeOrders.error,
      });
    } else {
      yield put({
        type: ActionTypes.GET_ACTIVE_ORDERS_SUCCESS,
        activeOrders,
      });
    }
  } catch (e) {
    if (e.error === ERRORS.UNAUTHORIZED) {
      yield put({type: ActionTypes.ERROR_LOGIN_EXPIRED});
    } else if (e.error === ERRORS.EXPIRED || e.code === ERRORS.NEED_AUTH) {
      yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
    }
    yield put({
      type: ActionTypes.GET_ACTIVE_ORDERS_FAILED,
      message: e.message,
    });
  }
}

function* cancelOrder(action) {
  try {
    if (!action.orderId) {
      return;
    }
    const getAuth = state => state.auth;
    const storeToken = yield select(getAuth);
    const headers = getBearerHeader(action.jjToken, storeToken);
    if (!headers) {
      yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
      return;
    }
    const order = yield call(orderApi.cancelOrder, action.orderId, headers);
    yield put({type: ActionTypes.CANCEL_ORDER_SUCCEEDED, order});
  } catch (e) {
    yield put({
      type: ActionTypes.CANCEL_ORDER_FAILED,
      message: e.message,
    });
  }
}

function* setRouteTracker(action) {
  try {
    const getAuth = state => state.auth;
    const storeToken = yield select(getAuth);
    const times = 3;
    const ms = 300;
    if (!storeToken) {
      storeToken = yield waitFor(getAuth, times, ms);
    }
    const headers = getBearerHeader(action.jjToken, storeToken);
    if (!headers) {
      yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
      return;
    }
    yield call(
      orderApi.setRouteTracker,
      action.salesId,
      action.enabled,
      headers
    );
  } catch (e) {
    // fire-and-forget — silently ignore errors
  }
}

function* trackOrder(action) {
  try {
    if (!action.axBasketId) {
      return;
    }
    const getAuth = state => state.auth;
    const storeToken = yield select(getAuth);
    const headers = getBearerHeader(action.jjToken, storeToken);
    const trackingData = yield call(
      orderApi.trackOrder,
      action.axBasketId,
      headers
    );
    yield put({type: ActionTypes.TRACK_ORDER_SUCCEEDED, trackingData});
  } catch (e) {
    yield put({
      type: ActionTypes.TRACK_ORDER_FAILED,
      message: e.message,
    });
  }
}

function* getInvoicedOrder(action) {
  try {
    const getAuth = state => state.auth;
    const storeToken = yield select(getAuth);
    const headers = getBearerHeader(action.jjToken, storeToken);
    const invoicedOrder = yield retry(
      MAX_ORDER_RETRY,
      GET_ORDER_RETRY_TIMEOUT,
      orderApi.getInvoicedOrder,
      action.salesId,
      action.accountId,
      headers
    );
    if (invoicedOrder) {
      try {
        if (isLessThanAWeekOldByDate(invoicedOrder.invoiceDateTime)) {
          invoicedOrder.couldBeRefunded = true;
        }
      } catch (e) {
        yield put({
          type: ActionTypes.GET_INVOICED_ORDER_FAILED,
          message: e.message,
        });
      }

      yield put({type: ActionTypes.GET_ORDER_SUCCEEDED, order: invoicedOrder});
    } else {
      yield put({type: ActionTypes.GET_INVOICED_ORDER_FAILED});
    }
  } catch (e) {
    yield put({
      type: ActionTypes.GET_INVOICED_ORDER_FAILED,
      message: e.message,
    });
  }
}

/**
 * A read still in flight when the confirmation page resets the order (on
 * leaving) must not land afterwards and leave that order in state for the
 * next page.
 */
export function* getFinalOrderUntilReset(action) {
  yield race({
    done: call(getFinalOrder, action),
    reset: take(ActionTypes.RESET_ORDERS_SUCCESS),
  });
}

export default function* getOrderYield() {
  yield takeLatest(ActionTypes.GET_ORDER_REQUESTED, getOrder);
  yield takeLatest(
    ActionTypes.GET_ORDER_AFTER_CHECKOUT_REQUESTED,
    getFinalOrderUntilReset
  );
  yield takeLatest(ActionTypes.CANCEL_ORDER_REQUESTED, cancelOrder);
  yield takeLatest(ActionTypes.GET_ORDER_HISTORY_REQUESTED, getOrderHistory);
  yield takeLatest(
    ActionTypes.GET_MORE_ORDER_HISTORY_REQUESTED,
    getOrderHistory
  );
  yield takeLatest(ActionTypes.TRACK_ORDER_REQUESTED, trackOrder);
  yield takeLatest(ActionTypes.SET_ROUTE_TRACKER_REQUESTED, setRouteTracker);
  yield takeLatest(ActionTypes.REAUTH_ORDER_REQUESTED, reAuthOrderPayment);
  yield takeLatest(ActionTypes.GET_INVOICED_ORDER_REQUESTED, getInvoicedOrder);
  yield takeLatest(
    ActionTypes.GET_CANCELED_ORDERS_REQUESTED,
    getCanceledOrders
  );
  yield takeLatest(ActionTypes.GET_ACTIVE_ORDERS_REQUESTED, getActiveOrders);
}
